// packages
import { index } from 'd3-array';
import { json } from 'd3-fetch';
import { derived, writable } from 'svelte/store';

// local
import { find } from './utils/array.js';
// import { test } from './testFeed.js';
// import { delTest } from './testDelFeed.js';
// import { testLA } from './testLA.js';
// import { ocTest } from './testOC.js';
import {
  DELEGATES_URL,
  LA_COUNTY_URL,
  STATE_URL,
  OC_URL,
  EMBED_URL,
  HLA_URL,
  DA_URL,
} from './constants.js';

/**
 * @template T
 * @typedef {import('svelte/store').Readable<T>} Readable
 */

/**
 * @typedef {import('./types').RaceFeed} RaceFeed
 * @typedef {import('./types').RaceInterface} RaceInterface
 */

/**
 * @typedef {import('./types').DelFeed} DelFeed
 * @typedef {import('./types').DelegatesInterface} DelegatesInterface
 */

/**
 * @typedef {import('./types').DelFeed} DelFeed
 * @typedef {import('./types').RaceInterface} RaceInterface
 */

/**
 * @param {{id: string}} d
 * @returns {string}
 */
const indexById = (d) => d.id;

/**
 * @param {{ap_id: string}} d
 * @returns {string}
 */
export const indexByApId = (d) => d.ap_id;

/**
 * @param {{code: string}} d
 * @returns {string}
 */
const indexByCode = (d) => d.code;

/**
 * @param {string} url The URL to request
 * @param {number} seconds How many seconds to wait between new requests
 * @returns {Promise<Readable<RaceFeed>>}
 */
export async function createFeed(url, seconds = 30) {
  // our timeout interval
  const interval = seconds * 1000;

  /**
   * We need to track the active timeout.
   * @type {number | undefined}
   */
  let timeoutId;

  /**
   * We need to keep track of the last run.
   * @type {number}
   */
  let lastUpdated;

  // set up our request function
  async function makeRequest() {
    console.debug('Making request:');

    // NOTED OUT TO USE LOCAL FEED
    let feed = '';
    if (url == DELEGATES_URL) {
      feed = /** @type {DelFeed} */ (await json(url));
    } else {
      feed = /** @type {RaceFeed} */ (await json(url));
    }
    // old
    // const feed = /** @type {RaceFeed} */ (await json(url));

    lastUpdated = Date.now();

    // for test

    // if (url == DELEGATES_URL) {
    //   // console.log('testing deltest', delTest);
    //   return delTest;
    //   // feed = /** @type {RaceFeed} */ (await json(url));
    //   // return feed;
    // } else if (url == STATE_URL) {
    //   return test;
    //   // feed = /** @type {RaceFeed} */ (await json(url));
    //   // return feed;
    // } else if (url == LA_COUNTY_URL) {
    //   // la test
    //   return testLA;
    // } else if (url == OC_URL) {
    //   return ocTest;
    // } else {
    //   feed = /** @type {RaceFeed} */ (await json(url));
    //   return feed;
    // }
    return feed;
  }

  // get the initial data
  const feed = await makeRequest();

  // create our store
  const { subscribe } = writable(feed, function start(set) {
    async function run() {
      // make our request
      const feed = await makeRequest();

      // pass it along
      // console.debug('Making update:', url);
      set(feed);

      timeoutId = window.setTimeout(run, interval);
    }

    const now = Date.now();
    // turn this on again for live
    if (now - lastUpdated < interval) {
      // we just had a run, so cool it
      timeoutId = window.setTimeout(run, interval);
    } else {
      // it's been a while, so get going!
      run();
    }

    return function stop() {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
  });

  return { subscribe };
}

/**
 * @param {Readable<RaceFeed>} store
 * @param {string} office
 */
export function createOfficeFeed(store, office) {
  return derived(store, ($feed) => {
    const feed = [];

    for (let idx = 0; idx < $feed.length; idx++) {
      const item = $feed[idx];

      if (item.office === office && item.race_type != 'G') {
        feed.push(item);
      }
    }
    const sorted = feed.sort((a, b) => a.seat_number - b.seat_number);
    return sorted;
    // return feed;
  });
}

/**
 * @param {Readable<RaceFeed>} store
 * @param {string} raceId
 */
export function createRaceFeed(store, raceId) {
  return derived(store, ($feed) => find($feed, (race) => race.id === raceId));
}

/**
 * @param {Readable<RaceInterface>} store
 * @param {string} candidateId
 */
export function createCandidateFeed(store, candidateId) {
  return derived(store, ($feed) =>
    find($feed.candidates, (candidate) => candidate.ap_id === candidateId)
  );
}

/**
 * @param {Readable<RaceFeed>} store
 */
export function createRaceLookup(store) {
  return derived(store, ($feed) => index($feed, indexById));
}

/**
 * @param {Readable<RaceInterface>} store
 */
export function createCandidateLookup(store) {
  return derived(store, ($race) => index($race.candidates, indexByApId));
}

/**
 * @param {Readable<RaceInterface>} store
 */
export function createSubunitLookup(store) {
  return derived(store, ($race) => index($race.subunits, indexByCode));
}

/**
 * @param {Readable<RaceLookup>} store
 * @param {string} raceId
 */

export function createRaceStore(store, raceId) {
  return derived(store, ($lookup) => $lookup.get(raceId));
}
